import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Swords, Eye, Flame, Clock, MapPin, BookOpenText, ChevronRight } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2N PR 1 — Per-Rivalry Feed.
 *
 * Cinematic, tense pulse of one rivalry pair. Renders inside the H2H surface
 * (and can be embedded anywhere we already display a rivalry). Tone is
 * emotional, never analytical — see narrative_style_guide.md.
 */

const KIND_ICON = {
  rivalry_live: Flame,
  rivalry_next_meeting: Clock,
  rivalry_drought: Eye,
  rivalry_location_shift: MapPin,
  rivalry_story: BookOpenText,
  rivalry_spectator_surge: Eye,
  rivalry_last_meeting: Swords,
  rivalry_recent_meeting: Swords,
};

const KIND_GLOW = {
  rivalry_live: 'from-rose-500/20 to-amber-500/10 border-rose-500/40',
  rivalry_next_meeting: 'from-amber-500/15 to-rose-500/5 border-amber-500/30',
  rivalry_drought: 'from-rose-500/15 to-slate-900/0 border-rose-500/25',
  rivalry_location_shift: 'from-cyan-500/10 to-amber-500/5 border-cyan-500/20',
  rivalry_story: 'from-purple-500/10 to-cyan-500/5 border-purple-500/25',
  rivalry_spectator_surge: 'from-emerald-500/10 to-cyan-500/5 border-emerald-500/25',
  rivalry_last_meeting: 'from-slate-700/30 to-slate-900/0 border-slate-700',
  rivalry_recent_meeting: 'from-slate-800/20 to-slate-900/0 border-slate-800',
};

export default function ArenaxRivalryFeed({ squadA, squadB, viewerSquadId, limit = 10 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!squadA || !squadB) return;
    setLoading(true);
    const params = new URLSearchParams({ limit: String(limit) });
    if (viewerSquadId) params.append('viewer_squad_id', viewerSquadId);
    api.get(`/arenax/rivalries/${squadA}/${squadB}/feed?${params.toString()}`)
      .then(r => setData(r.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [squadA, squadB, viewerSquadId, limit]);

  if (loading) return null;
  if (!data || !data.rivalry || data.items.length === 0) return null;

  const r = data.rivalry;
  const hasDoc = (r.matches_count || 0) >= 3;
  return (
    <div className="rounded-2xl border border-rose-500/25 overflow-hidden"
      data-testid="arenax-rivalry-feed"
      style={{ background: 'linear-gradient(160deg, rgba(244,63,94,0.06), var(--ax-card-bg-soft))' }}>
      <div className="px-5 pt-5 pb-3 border-b border-rose-500/15">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <Swords className="h-4 w-4 text-rose-300" />
            <span className="text-[10px] tracking-[0.3em] text-rose-300 font-bold uppercase">Rivalry pulse</span>
          </div>
          {hasDoc && (
            <Link to={`/arenax/rivalries/${r.squad_a_id}/${r.squad_b_id}/documentary`}
              className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider
                           text-rose-300 hover:text-rose-200 transition-colors"
              data-testid="arenax-rivalry-feed-doc-link">
              <BookOpenText className="h-3 w-3" />
              Documentary
              <ChevronRight className="h-3 w-3" />
            </Link>
          )}
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xl sm:text-2xl font-bold tracking-tight text-slate-100 truncate">
              {r.squad_a_name} <span className="text-slate-500">vs</span> {r.squad_b_name}
            </div>
            <div className="mt-1 text-sm text-slate-400 italic">
              {r.retrospective}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-xs text-slate-500">H2H</div>
            <div className="text-lg font-bold text-slate-100">{r.wins}–{r.losses}{r.draws ? `–${r.draws}` : ''}</div>
          </div>
        </div>
      </div>

      <div className="divide-y divide-rose-500/10">
        {data.items.map((item, i) => (
          <RivalryFeedCard key={`${item.kind}_${i}`} item={item} />
        ))}
      </div>
    </div>
  );
}

function RivalryFeedCard({ item }) {
  const Icon = KIND_ICON[item.kind] || Swords;
  const glow = KIND_GLOW[item.kind] || KIND_GLOW.rivalry_recent_meeting;
  const content = (
    <div className={`flex items-start gap-3 px-5 py-3.5 bg-gradient-to-r ${glow} hover:bg-rose-500/5 transition-all`}
      data-testid={`arenax-rivalry-feed-item-${item.kind}`}>
      <div className="shrink-0 mt-0.5">
        <Icon className="h-4 w-4 text-rose-200/80" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[15px] text-slate-100 leading-snug">{item.narrative}</div>
        {item.hours_to_go !== undefined && item.hours_to_go !== null && item.hours_to_go > 0 && (
          <div className="mt-1 text-[11px] text-rose-300/80 font-mono">
            ⏱ in {item.hours_to_go < 1 ? '<1' : Math.round(item.hours_to_go)}h
          </div>
        )}
      </div>
      {item.deeplink && <ChevronRight className="h-4 w-4 text-slate-500 shrink-0 mt-1" />}
    </div>
  );
  if (item.deeplink) {
    return <Link to={item.deeplink} className="block">{content}</Link>;
  }
  return content;
}
